import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-hot-toast';
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from 'recharts';
import {
  fetchAllDetections,
  fetchAvailableDates,
  fetchDetections,
  fetchSummary
} from '../api';
import DateSelector from '../components/DateSelector';

const CHART_COLORS = ['#2f522a', '#129dc7', '#d9a916', '#c0392b', '#5f9a3d', '#1a8fa3'];

function parseDateKey(dateKey) {
  const [dd, mm, yyyy] = dateKey.split('-').map(Number);
  return new Date(yyyy, mm - 1, dd);
}

function sortDateKeys(keys) {
  return [...keys].sort((a, b) => parseDateKey(a) - parseDateKey(b));
}

function KpiCard({ title, value, sub }) {
  return (
    <article className="analytics-kpi">
      <p>{title}</p>
      <h3>{value}</h3>
      <small>{sub}</small>
    </article>
  );
}

function ChartPanel({ title, subtitle, children }) {
  return (
    <section className="analytics-panel">
      <div className="analytics-panel__head">
        <h3>{title}</h3>
        <span>{subtitle}</span>
      </div>
      <div className="analytics-panel__body">{children}</div>
    </section>
  );
}

export default function Analytics() {
  const [dateOptions, setDateOptions] = useState([{ key: 'today', label: 'Today' }]);
  const [selectedDate, setSelectedDate] = useState('today');
  const [summary, setSummary] = useState(null);
  const [dayDetections, setDayDetections] = useState([]);
  const [allDetections, setAllDetections] = useState([]);
  const [loading, setLoading] = useState(true);

  const selectedDateLabel = useMemo(() => {
    const found = dateOptions.find((item) => item.key === selectedDate);
    return found?.label || selectedDate;
  }, [dateOptions, selectedDate]);

  useEffect(() => {
    async function loadDatesAndHistory() {
      try {
        const [datesRes, allRes] = await Promise.all([
          fetchAvailableDates(),
          fetchAllDetections(10000)
        ]);
        const dates = datesRes.data?.dates || [];
        const sorted = [...dates].sort((a, b) => {
          if (a.key === 'today') return -1;
          if (b.key === 'today') return 1;
          return 0;
        });
        setDateOptions(sorted.length ? sorted : [{ key: 'today', label: 'Today' }]);
        setAllDetections(allRes.data?.detections || []);
      } catch (err) {
        toast.error('Failed to load analytics data');
      }
    }
    loadDatesAndHistory();
  }, []);

  useEffect(() => {
    async function loadSelectedDateData() {
      setLoading(true);
      try {
        const [sumRes, dayRes] = await Promise.all([
          fetchSummary(selectedDate),
          fetchDetections(selectedDate, 'all', 1000)
        ]);
        setSummary(sumRes.data);
        setDayDetections(dayRes.data?.detections || []);
      } catch (err) {
        toast.error('Failed to load date analytics');
      } finally {
        setLoading(false);
      }
    }
    loadSelectedDateData();
  }, [selectedDate]);

  const unresolvedCount = useMemo(
    () => dayDetections.filter((row) => !row.resolved).length,
    [dayDetections]
  );

  const avgConfidence = useMemo(() => {
    if (!dayDetections.length) return '0.0';
    const total = dayDetections.reduce((sum, row) => sum + Number(row.confidence || 0), 0);
    return (total / dayDetections.length).toFixed(1);
  }, [dayDetections]);

  // With small counts, classes often tie for the lead - list every class
  // tied for first instead of arbitrarily picking one (see StatsSummary.jsx
  // for the same fix on the Dashboard/History stat cards).
  const topClass = useMemo(() => {
    const byClass = summary?.by_class || [];
    if (!byClass.length) return 'None';
    const topCount = byClass[0].count;
    return byClass.filter((row) => row.count === topCount).map((row) => row.class_name).join(' / ');
  }, [summary]);

  const dayClassData = useMemo(
    () => (summary?.by_class || []).map((row) => ({ name: row.class_name, value: row.count })),
    [summary]
  );

  const dayCameraData = useMemo(
    () => (summary?.by_camera || []).map((row) => ({ camera: row.camera, count: row.count })),
    [summary]
  );

  const hourlyData = useMemo(() => {
    const buckets = Array.from({ length: 24 }, (_, h) => ({
      hour: `${String(h).padStart(2, '0')}:00`,
      count: 0
    }));
    dayDetections.forEach((row) => {
      const hour = Number((row.timestamp || '00:00:00').split(':')[0]);
      if (!Number.isNaN(hour) && buckets[hour]) {
        buckets[hour].count += 1;
      }
    });
    return buckets;
  }, [dayDetections]);

  const dailyTrendData = useMemo(() => {
    const map = new Map();
    allDetections.forEach((row) => {
      const key = row.date;
      map.set(key, (map.get(key) || 0) + 1);
    });
    return sortDateKeys([...map.keys()]).map((date) => ({ date, total: map.get(date) }));
  }, [allDetections]);

  const classTrendData = useMemo(() => {
    const classTotals = new Map();
    allDetections.forEach((row) => {
      const name = row.class_name || 'Unknown';
      classTotals.set(name, (classTotals.get(name) || 0) + 1);
    });

    const topClasses = [...classTotals.entries()]
      .sort((a, b) => b[1] - a[1])
      .slice(0, 4)
      .map(([name]) => name);

    const byDate = new Map();
    allDetections.forEach((row) => {
      const date = row.date;
      const cls = row.class_name || 'Unknown';
      if (!topClasses.includes(cls)) return;
      if (!byDate.has(date)) {
        byDate.set(date, { date });
      }
      const obj = byDate.get(date);
      obj[cls] = (obj[cls] || 0) + 1;
    });

    return {
      series: sortDateKeys([...byDate.keys()]).map((date) => byDate.get(date)),
      keys: topClasses
    };
  }, [allDetections]);

  return (
    <div className="page-grid">
      <section className="control-bar analytics-control-bar">
        <DateSelector options={dateOptions} value={selectedDate} onChange={setSelectedDate} />
        <p>Business Analytics View for {selectedDateLabel}</p>
      </section>

      <section className="analytics-kpi-grid">
        <KpiCard
          title="Total Defects (Selected Day)"
          value={summary?.total_alerts ?? 0}
          sub="Overall contamination events"
        />
        <KpiCard title="Top Defect Class" value={topClass} sub="Highest count class today" />
        <KpiCard title="Unresolved Alerts" value={unresolvedCount} sub="Needs operator action" />
        <KpiCard title="Avg Confidence" value={`${avgConfidence}%`} sub="Model confidence mean" />
      </section>

      <section className="analytics-grid analytics-grid--two">
        <ChartPanel title="Defect Class Mix" subtitle="Selected day breakdown">
          <ResponsiveContainer width="100%" height={280}>
            <PieChart>
              <Pie dataKey="value" data={dayClassData} nameKey="name" outerRadius={90} label>
                {dayClassData.map((entry, idx) => (
                  <Cell key={entry.name} fill={CHART_COLORS[idx % CHART_COLORS.length]} />
                ))}
              </Pie>
              <Tooltip />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </ChartPanel>

        <ChartPanel title="Camera-Wise Defects" subtitle="Performance by camera">
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={dayCameraData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="camera" />
              <YAxis allowDecimals={false} />
              <Tooltip />
              <Bar dataKey="count" fill="#2f522a" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartPanel>
      </section>

      <section className="analytics-grid analytics-grid--two">
        <ChartPanel title="Historical Daily Trend" subtitle="Defect totals over all dates">
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={dailyTrendData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" />
              <YAxis allowDecimals={false} />
              <Tooltip />
              <Legend />
              <Line
                type="monotone"
                dataKey="total"
                name="Total defects"
                stroke="#d9a916"
                strokeWidth={3}
                dot={{ r: 4 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </ChartPanel>

        <ChartPanel title="Hourly Defect Pattern" subtitle="Selected day by hour">
          <ResponsiveContainer width="100%" height={300}>
            <AreaChart data={hourlyData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="hour" interval={2} />
              <YAxis allowDecimals={false} />
              <Tooltip />
              <Area type="monotone" dataKey="count" stroke="#129dc7" fill="#e2f5f8" />
            </AreaChart>
          </ResponsiveContainer>
        </ChartPanel>
      </section>

      <section className="analytics-grid">
        <ChartPanel title="Class Trends Across Dates" subtitle="Top classes over time">
          <ResponsiveContainer width="100%" height={320}>
            <BarChart data={classTrendData.series}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" />
              <YAxis allowDecimals={false} />
              <Tooltip />
              <Legend />
              {classTrendData.keys.map((key, idx) => (
                <Bar
                  key={key}
                  dataKey={key}
                  stackId="classes"
                  fill={CHART_COLORS[idx % CHART_COLORS.length]}
                  radius={[4, 4, 0, 0]}
                />
              ))}
            </BarChart>
          </ResponsiveContainer>
        </ChartPanel>
      </section>

      {!loading && !dayDetections.length && (
        <section className="analytics-empty">
          No detections available for the selected date yet.
        </section>
      )}
    </div>
  );
}
